import authService from '../../services/authService';
import { defaultObjetFromFilename, detectDateFromFilename } from '../../utils/depotFileDate';
import { matchClientFromFilename } from '../../utils/ragClientMatch';

export const MAX_BATCH_BYTES = 10 * 1024 * 1024 * 1024;
export const MAX_FILES = 50;
export const HISTORY_RECENT_LIMIT = 10;
export const HISTORY_ALL_LIMIT = 100;

export const AUDIO_EXTENSIONS = ['m4a', 'mp3', 'wav', 'mp4', 'aac', 'ogg'];
export const DOCUMENT_EXTENSIONS = [
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
  'odt', 'txt', 'md', 'jpg', 'jpeg', 'png', 'zip',
];

export const TYPE_RDV_OPTIONS = [
  { value: 'rdv_commercial', label: 'RDV commercial' },
  { value: 'reunion_suivi', label: 'Réunion de suivi' },
  { value: 'atelier', label: 'Atelier' },
  { value: 'autre', label: 'Autre' },
];

export const DEFAULT_DESTINATION = 'Comptes rendus';

export const getDriveClientValue = (client) => client.folder_id;
export const getDriveClientLabel = (client) => client.nom;

export function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export function currentMonthIso() {
  return new Date().toISOString().slice(0, 7);
}

export function formatFileSize(bytes) {
  if (!bytes) return '0 o';
  if (bytes >= 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} Go`;
  }
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  }
  return `${Math.round(bytes / 1024)} Ko`;
}

export function filesTotalBytes(items) {
  return items.reduce((sum, item) => (item.file ? sum + item.file.size : sum), 0);
}

export function fileCount(items) {
  return items.filter((item) => item.file).length;
}

export function formatDisplayDate(value) {
  if (!value) return '—';
  if (/^\d{4}-\d{2}$/.test(value)) {
    const [y, m] = value.split('-');
    const date = new Date(Number(y), Number(m) - 1, 1);
    return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const date = new Date(`${value}T12:00:00`);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function getTypeRdvLabel(value) {
  return TYPE_RDV_OPTIONS.find((option) => option.value === value)?.label ?? value ?? '—';
}

export function getDestinationLabel(value, destinations) {
  return destinations.find((item) => item.value === value)?.label ?? value ?? '—';
}

export function matchDepositorPersonneId(personnes) {
  const email = (authService.getCurrentUser()?.email || '').trim().toLowerCase();
  if (!email) return '';
  const found = (personnes || []).find((item) => (item.email || '').trim().toLowerCase() === email);
  return found ? String(found.id) : '';
}

export function personneIdFromCommercialId(personnes, commercialId) {
  if (!commercialId) return '';
  const found = (personnes || []).find((item) => String(item.commercial_id || '') === String(commercialId));
  return found ? String(found.id) : '';
}

export function splitPersonneSelection(personnes, optionId) {
  const found = (personnes || []).find((item) => String(item.id) === String(optionId));
  return {
    userId: found?.user_id ? String(found.user_id) : '',
    commercialId: found?.commercial_id ? String(found.commercial_id) : '',
  };
}

export function inferTypeDepotFromFile(file) {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (AUDIO_EXTENSIONS.includes(extension)) return 'audio_rdv';
  if (DOCUMENT_EXTENSIONS.includes(extension)) return 'document';
  return null;
}

export function validateDroppedFile(file) {
  if (!file) {
    return 'Ajoutez un fichier.';
  }
  const typeDepot = inferTypeDepotFromFile(file);
  if (!typeDepot) {
    return `« ${file.name} » : format non accepté (audio ou document).`;
  }
  return null;
}

export function createPendingFileItem(file, defaults) {
  const typeDepot = inferTypeDepotFromFile(file);
  const detected = detectDateFromFilename(file.name);
  const isDoc = typeDepot === 'document';
  const matchedClient = matchClientFromFilename(file.name, defaults.clients || []);
  const matchedFolderId = matchedClient ? getDriveClientValue(matchedClient) : '';

  return {
    id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
    kind: 'file',
    typeDepot,
    file,
    date: isDoc
      ? (detected.month || defaults.dateDocument)
      : (detected.date || defaults.dateRdv),
    dateFromFilename: Boolean(detected.source),
    clientFolderId: matchedFolderId || defaults.clientFolderId || '',
    clientFromFilename: Boolean(matchedFolderId),
    commercialId: defaults.commercialId || '',
    commercialSource: defaults.commercialSource || null,
    objet: isDoc ? defaultObjetFromFilename(file.name) : '',
    typeRdv: defaults.typeRdv || 'rdv_commercial',
    texte: '',
    status: 'pending',
    progress: 0,
    error: null,
  };
}

export function createPendingNotesItem(defaults) {
  return {
    id: `notes-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    kind: 'notes',
    typeDepot: 'notes',
    file: null,
    date: defaults.dateDocument,
    dateFromFilename: false,
    clientFolderId: defaults.clientFolderId || '',
    clientFromFilename: false,
    commercialId: defaults.commercialId || '',
    commercialSource: defaults.commercialSource || null,
    objet: '',
    typeRdv: '',
    texte: '',
    status: 'pending',
    progress: 0,
    error: null,
  };
}

export function depotAwaitingValidation(depot) {
  if (depot?.attente_validation) return true;
  if (depot?.statut === 'a_valider') return true;
  return depot?.statut === 'termine'
    && depot?.validation_requise
    && Boolean(depot?.doc_url);
}

/** BEBEDC après l’analyse normale : le compte rendu existe, la relecture est déjà passée. */
export function depotCanRequestBebedcLater(depot) {
  if (!depot?.doc_url || depot.bebedc_url) return false;
  if (depot.statut === 'erreur') return false;
  if (depotAwaitingValidation(depot)) return false;
  return depot.statut === 'termine' || depot.statut === 'en_cours';
}
